import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Database, History, ShieldCheck, Play, HardDrive, RefreshCw,
  Cloud, Plus, Trash2, Star, Download, X, Info, CheckCircle2, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import { useFontSize } from './FontSizeContext';

const PROVIDER_ICON_COLOR = {
  gdrive: 'text-yellow-500',
  onedrive: 'text-blue-500',
  dropbox: 'text-indigo-500',
};

const HISTORY_PAGE_SIZE = 10;

const BackupManager = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState('history');
  const [isProcessing, setIsProcessing] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [backupHistory, setBackupHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyReload, setHistoryReload] = useState(0);
  const refreshHistory = () => setHistoryReload(n => n + 1);

  const [providersLoading, setProvidersLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newConn, setNewConn] = useState({ provider: '', client_id: '', client_secret: '' });
  const [savingConn, setSavingConn] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { 'Authorization': `Bearer ${token}` } };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const res = await axios.get('/api/admin/backup/history', {
          ...authHeaders,
          params: { page: historyPage, limit: HISTORY_PAGE_SIZE },
        });
        if (cancelled) return;
        setBackupHistory(res.data.data || []);
        setHistoryTotal(res.data.total || 0);
      } catch (err) {
        if (!cancelled) toast.error(t('backup.messages.historyFetchError'));
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [historyPage, historyReload, t]);

  const fetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const res = await axios.get('/api/admin/backup/providers', authHeaders);
      setConnections(res.data.connections || []);
      setAvailableProviders(res.data.available_providers || []);
    } catch (err) {
      toast.error(t('backup.messages.providersFetchError'));
    } finally {
      setProvidersLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // 🎯 بعد رجوع المستخدم من صفحة موافقة المزود (OAuth) بيرجعله هنا برابط
  // فيه ?backup_oauth_success=1 أو ?backup_oauth_error=...
  useEffect(() => {
    const success = searchParams.get('backup_oauth_success');
    const error = searchParams.get('backup_oauth_error');
    if (success) {
      toast.success(t('backup.messages.oauthSuccess'));
      setActiveTab('providers');
      fetchProviders();
    } else if (error) {
      toast.error(t('backup.messages.oauthError'));
      setActiveTab('providers');
    }
    if (success || error) {
      setSearchParams(prev => {
        prev.delete('backup_oauth_success');
        prev.delete('backup_oauth_error');
        return prev;
      }, { replace: true });
    }
  }, []);

  const handleStartBackup = async () => {
    setIsProcessing(true);
    const loadId = toast.loading(t('backup.messages.starting'));
    try {
      await axios.post('/api/admin/backup/run', {}, authHeaders);
      toast.success(t('backup.messages.started'), { id: loadId });
      setTimeout(() => { setHistoryPage(1); refreshHistory(); }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('backup.messages.startFailed'), { id: loadId });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async (log) => {
    const loadId = toast.loading(t('backup.messages.downloading'));
    try {
      const res = await axios.get(`/api/admin/backup/download/${log.id}`, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', log.filename || `backup_${log.id}.sql.gz`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success(t('backup.messages.downloadReady'), { id: loadId });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('backup.messages.downloadFailed'), { id: loadId });
    }
  };

  const handleAddConnection = async () => {
    if (!newConn.provider || !newConn.client_id || !newConn.client_secret) {
      toast.error(t('backup.messages.fillAllFields'));
      return;
    }
    setSavingConn(true);
    try {
      const res = await axios.post('/api/admin/backup/providers', newConn, authHeaders);
      // 🎯 بعد ما نسجل الاتصال، لازم نودي المستخدم لصفحة موافقة المزود
      // نفسه (Google/Microsoft/Dropbox) - ده مش XHR ممكن نتابعه، لازم
      // تحويل كامل للصفحة
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('backup.messages.connectFailed'));
      setSavingConn(false);
    }
  };

  const handleDeleteConnection = (conn) => {
    Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure'),
      text: t('backup.messages.disconnectConfirm', { label: conn.account_label || conn.label }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.delete'),
      cancelButtonText: t('common.cancel'),
      reverseButtons: isRTL,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/api/admin/backup/providers/${conn.id}`, authHeaders);
        toast.success(t('backup.messages.disconnected'));
        fetchProviders();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('backup.messages.disconnectFailed'));
      }
    });
  };

  const handleActivateConnection = async (conn) => {
    try {
      await axios.post(`/api/admin/backup/providers/${conn.id}/activate`, {}, authHeaders);
      toast.success(t('backup.messages.activated', { label: conn.account_label || conn.label }));
      fetchProviders();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('backup.messages.activateFailed'));
    }
  };

  const activeConnection = connections.find(c => c.is_active);
  const formatSize = (bytes) => {
    if (!bytes) return '—';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
  };
  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  const STATUS_STYLES = {
    SUCCESS: 'bg-green-100 text-green-700',
    SUCCESS_LOCAL_ONLY: 'bg-blue-100 text-blue-700',
    RUNNING: 'bg-yellow-100 text-yellow-700 animate-pulse',
    FAILED: 'bg-red-100 text-red-700',
  };

  return (
    <div className={`p-8 bg-[#f3f4f6] min-h-screen font-black uppercase text-[0.85em] ${isRTL ? 'text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header Card */}
        <div className="bg-white rounded shadow-xl p-8 border-t-4 border-[#1a3353]">
          <div className={`flex justify-between items-center border-b pb-6 mb-8`}>
            <div className={`flex items-center gap-4`}>
              <div className="bg-blue-100 p-3 rounded-lg text-[#1a3353]">
                <Database size={24}/>
              </div>
              <div>
                <h2 className="text-[1.38em] text-[#1a3353] font-black tracking-tighter normal-case">{t('backup.title')}</h2>
                <p className="text-gray-400 text-[0.69em] normal-case font-semibold">{t('backup.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-100">
              <ShieldCheck size={14}/> {t('backup.systemSecure')}
            </div>
          </div>

          {/* Tabs */}
          <div className={`flex gap-2 border-b mb-6`}>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'history' ? 'border-[#1a3353] text-[#1a3353]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <History size={14}/> {t('backup.tabs.history')}
            </button>
            <button
              onClick={() => setActiveTab('providers')}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'providers' ? 'border-[#1a3353] text-[#1a3353]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <Cloud size={14}/> {t('backup.tabs.providers')}
            </button>
          </div>

          {activeTab === 'history' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
              <div className="p-6 bg-slate-50 rounded border border-dashed border-gray-300 relative overflow-hidden">
                <h4 className="text-blue-900 mb-4 flex items-center gap-2 font-black">
                  <HardDrive size={16}/> {t('backup.activeProvider')}
                </h4>
                {activeConnection ? (
                  <div className="space-y-2 relative z-10">
                    <div className="flex justify-between"><span>{t('backup.provider')}:</span> <span className="text-blue-600 font-black">{activeConnection.label}</span></div>
                    <div className="flex justify-between"><span>{t('backup.account')}:</span> <span className="text-gray-600 font-black normal-case">{activeConnection.account_label || '—'}</span></div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                       <span>{t('backup.status')}:</span>
                       <span className="text-green-600">{t('backup.connected')}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 normal-case font-semibold italic text-[0.77em]">{t('backup.noActiveProvider')}</p>
                )}
              </div>

              <div className="flex flex-col justify-center space-y-4">
                <button
                  disabled={isProcessing}
                  onClick={handleStartBackup}
                  className={`w-full py-5 rounded shadow-lg font-black flex items-center justify-center gap-3 transition-all active:scale-95
                  ${isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1a3353] hover:bg-black text-white'}`}
                >
                  {isProcessing ? <RefreshCw className="animate-spin" size={20}/> : <Play size={20} fill="white"/>}
                  {isProcessing ? t('backup.processing') : t('backup.runButton')}
                </button>
                <p className="text-[0.69em] text-gray-400 text-center italic normal-case font-semibold">
                  {t('backup.runHint')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'providers' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => { setNewConn({ provider: availableProviders[0]?.id || '', client_id: '', client_secret: '' }); setShowAddModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1a3353] text-white rounded font-black hover:bg-black transition-all"
                >
                  <Plus size={14}/> {t('backup.addConnection')}
                </button>
              </div>

              {providersLoading ? (
                <div className="text-center py-10 text-gray-300 font-black italic">{t('common.loading')}</div>
              ) : connections.length === 0 ? (
                <div className="text-center py-10 text-gray-300 font-black italic">{t('backup.noConnections')}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {connections.map(conn => (
                    <div key={conn.id} className={`p-4 border rounded relative ${conn.is_active ? 'border-green-300 bg-green-50/40' : 'border-gray-200 bg-slate-50'}`}>
                      <div className={`flex items-center justify-between mb-3`}>
                        <div className={`flex items-center gap-2`}>
                          <Cloud size={18} className={PROVIDER_ICON_COLOR[conn.provider] || 'text-gray-500'}/>
                          <span className="text-blue-900">{conn.label}</span>
                        </div>
                        {conn.is_active && (
                          <span className="flex items-center gap-1 text-green-600 text-[0.69em] bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={11}/> {t('backup.active')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-[0.77em] text-gray-500 font-semibold normal-case">
                        <div>{t('backup.account')}: <span className="text-gray-700">{conn.account_label || (conn.connected ? '—' : t('backup.pendingAuth'))}</span></div>
                        <div className="font-mono text-gray-400">{conn.client_id_masked}</div>
                      </div>
                      <div className={`flex items-center gap-2 mt-4`}>
                        {conn.connected && !conn.is_active && (
                          <button
                            onClick={() => handleActivateConnection(conn)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-900 text-white rounded text-[0.69em] hover:bg-black"
                          >
                            <Star size={11}/> {t('backup.setActive')}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteConnection(conn)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded text-[0.69em] hover:bg-red-100"
                        >
                          <Trash2 size={11}/> {t('backup.disconnect')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* History Table */}
        {activeTab === 'history' && (
          <div className="bg-white rounded shadow-xl overflow-hidden border-t-4 border-orange-500">
             <div className={`p-4 bg-slate-50 border-b flex justify-between items-center`}>
                <h3 className={`text-blue-900 flex items-center gap-2 font-black italic`}>
                  <History size={16}/> {t('backup.history.title')}
                </h3>
                <button onClick={refreshHistory} className="text-gray-400 hover:text-blue-900">
                  <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''}/>
                </button>
             </div>
             <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-gray-50 text-gray-400 text-[0.69em] border-b">
                  <tr>
                    <th className="p-4">{t('backup.history.filename')}</th>
                    <th className="p-4">{t('backup.history.date')}</th>
                    <th className="p-4">{t('backup.history.size')}</th>
                    <th className="p-4">{t('backup.history.provider')}</th>
                    <th className="p-4 text-center">{t('backup.history.status')}</th>
                    <th className="p-4 text-center">{t('backup.history.download')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y text-gray-600">
                  {historyLoading ? (
                    <tr><td colSpan="6" className="p-10 text-center text-gray-300 italic font-black">{t('common.loading')}</td></tr>
                  ) : backupHistory.length > 0 ? backupHistory.map(log => (
                    <tr key={log.id} className="hover:bg-blue-50/50">
                      <td className="p-4 font-mono text-blue-800 normal-case">{log.filename || '—'}</td>
                      <td className="p-4 normal-case">{formatDate(log.started_at)}</td>
                      <td className="p-4 normal-case">{formatSize(log.size_bytes)}</td>
                      <td className="p-4 normal-case">{log.provider || '—'}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-0.5 rounded text-[0.69em] ${STATUS_STYLES[log.status] || 'bg-gray-100 text-gray-600'}`}>{t(`backup.history.statusValues.${log.status}`, log.status)}</span>
                      </td>
                      <td className="p-4 text-center">
                        {log.downloadable ? (
                          <button onClick={() => handleDownload(log)} className="text-blue-700 hover:text-black inline-flex" title={t('backup.download')}>
                            <Download size={16}/>
                          </button>
                        ) : log.status === 'SUCCESS' ? (
                          <span className="inline-flex items-center gap-1 text-gray-400 normal-case text-[0.69em]" title={t('backup.noLocalCopy')}>
                            <Cloud size={12}/> {t('backup.cloudOnly', 'Cloud only')}
                          </span>
                        ) : (
                          <span className="text-gray-300 normal-case text-[0.69em]" title={t('backup.noLocalCopy')}>—</span>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="p-10 text-center text-gray-300 italic font-black">{t('backup.history.empty')}</td>
                    </tr>
                  )}
                </tbody>
             </table>
             {historyTotal > 0 && (() => {
               const totalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
               const from = (historyPage - 1) * HISTORY_PAGE_SIZE + 1;
               const to = Math.min(historyPage * HISTORY_PAGE_SIZE, historyTotal);
               const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
               const NextIcon = isRTL ? ChevronLeft : ChevronRight;
               const navBtn = 'p-1.5 rounded border bg-white text-blue-900 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed';
               return (
                 <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
                   <span className="text-gray-400 text-[0.69em] normal-case font-semibold">
                     {t('backup.pageInfo', { from, to, total: historyTotal, defaultValue: `${from}–${to} / ${historyTotal}` })}
                   </span>
                   <div className="flex items-center gap-2">
                     <button className={navBtn} disabled={historyPage <= 1 || historyLoading} onClick={() => setHistoryPage(p => p - 1)}>
                       <PrevIcon size={14}/>
                     </button>
                     <span className="text-blue-900 text-[0.77em] min-w-[3.5em] text-center">{historyPage} / {totalPages}</span>
                     <button className={navBtn} disabled={historyPage >= totalPages || historyLoading} onClick={() => setHistoryPage(p => p + 1)}>
                       <NextIcon size={14}/>
                     </button>
                   </div>
                 </div>
               );
             })()}
          </div>
        )}
      </div>

      {/* Add Connection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-2xl overflow-hidden">
            <div className={`bg-[#1a3353] p-4 flex justify-between items-center text-white`}>
              <h2 className="font-black normal-case">{t('backup.addConnection')}</h2>
              <button onClick={() => setShowAddModal(false)} className="hover:bg-white/20 p-1 rounded"><X size={18}/></button>
            </div>
            <div className="p-6 space-y-4 normal-case font-bold">
              <div className="flex flex-col gap-2">
                <label className="text-gray-400 uppercase text-[0.77em]">{t('backup.providerType')}</label>
                <select
                  className="p-3 border rounded bg-gray-50 text-blue-900 outline-none"
                  value={newConn.provider}
                  onChange={(e) => setNewConn({ ...newConn, provider: e.target.value })}
                >
                  {availableProviders.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-gray-400 uppercase text-[0.77em]">{t('backup.clientId')}</label>
                <input
                  className="p-3 border rounded outline-none focus:border-blue-500 font-mono text-[0.85em]"
                  placeholder={t('backup.clientIdPlaceholder')}
                  value={newConn.client_id}
                  onChange={(e) => setNewConn({ ...newConn, client_id: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-gray-400 uppercase text-[0.77em]">{t('backup.clientSecret')}</label>
                <input
                  type="password"
                  className="p-3 border rounded outline-none focus:border-blue-500 font-mono text-[0.85em]"
                  placeholder={t('backup.clientSecretPlaceholder')}
                  value={newConn.client_secret}
                  onChange={(e) => setNewConn({ ...newConn, client_secret: e.target.value })}
                />
              </div>
              <div className={`p-4 bg-yellow-50 rounded border border-yellow-100 flex gap-3 text-yellow-800`}>
                <Info size={20} className="shrink-0"/>
                <p className="normal-case font-medium leading-relaxed text-[0.85em]">
                  {t('backup.consoleHint')}
                  {' '}
                  {availableProviders.find(p => p.id === newConn.provider)?.console_url && (
                    <a href={availableProviders.find(p => p.id === newConn.provider).console_url} target="_blank" rel="noreferrer" className="underline text-blue-700">
                      {t('backup.openConsole')}
                    </a>
                  )}
                </p>
              </div>
              <button
                disabled={savingConn}
                onClick={handleAddConnection}
                className={`w-full py-4 text-white rounded font-black flex items-center justify-center gap-2 shadow-lg transition-all ${savingConn ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-black'}`}
              >
                {savingConn ? <RefreshCw className="animate-spin" size={16}/> : <Cloud size={16}/>} {t('backup.connectButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManager;
